import type { ResultsAiView } from 'definitions';
import type { Experiment } from 'types';
import { useRef } from 'react';
import { RESULTS_AI_GOALS } from 'definitions';
import { ALL_CURRENCIES } from 'helpers/currency';
import { useReportResultsAiView } from '@/state/results-ai/useResultsAi';
import { Selectors } from './Selectors';
import { RecommendationBanner } from './RecommendationBanner';
import { StatisticsTable } from './StatisticsTable';
import { SignificanceTable } from './SignificanceTable';
import { EvaluationControls } from './EvaluationControls';
import { ChartTabs } from './ChartTabs';
import { variantColorOrder } from './control-options';
import { PowerAnalysis } from './PowerAnalysis';
import { StickyControlBar } from './StickyControlBar';
import { SampleRatioCheck } from './SampleRatioCheck';
import { RateOverTime } from './RateOverTime';
import { Segments } from './Segments';
import { ReportSection } from './ReportSection';
import { SecondaryObjectives } from './SecondaryObjectives';
import { processChartData } from './StatisticsChart';
import { useExperimentStats } from '@/state/experiments/useExperimentStats';

type ExperimentStatisticsScreenProps = {
  experiment: Experiment;
  /** Called once a variant has been deployed. The details screen decides whether to offer the debrief. */
  onDeployed: () => Promise<void>;
};

type StatsManager = ReturnType<typeof useExperimentStats>;

/** Collapsed-state summaries, so a closed section is still worth finding again. */
const significanceSummary = (manager: StatsManager): string => {
  const significance = manager.significance ?? [];
  if (significance.length === 0) return 'no comparison yet';
  const winners = significance.filter((item) => item.significant).length;
  return winners > 0 ? `${winners} of ${significance.length} significant` : 'none significant';
};

const powerSummary = (manager: StatsManager): string =>
  manager.power ? `${Math.round(manager.power.power * 100)}% power` : 'not enough data';

const secondarySummary = (manager: StatsManager): string => {
  const count = manager.secondaryObjectives.length;
  if (count === 0) return 'none selected';

  // Counts the ones with data rather than the ones selected: a mapped objective that has
  // recorded nothing yet is common on GA4 sites and should not read as covered.
  const withData = manager.secondaryObjectives.filter((event) =>
    Object.values(manager.secondaryStats?.[event] ?? {}).some((row) => row.conversions > 0),
  ).length;

  return withData === count ? `${count} tracked` : `${withData} of ${count} with data`;
};

/** The slice of the numbers on screen, which the results AI reads (the server checks every value). */
const resultsView = (manager: StatsManager): ResultsAiView => ({
  currency: manager.currency ?? ALL_CURRENCIES,
  goal: RESULTS_AI_GOALS.find((goal) => goal === manager.goal) ?? 'Conversions',
  method: manager.evaluationMethod,
  correction: manager.correctionMethod,
});

/** "Conversions · All currencies": what the chat's answers are about, in its header. */
const viewLabel = (manager: StatsManager): string => {
  const currency = manager.currency && manager.currency !== ALL_CURRENCIES ? manager.currency : 'All currencies';
  return `${manager.goalLabels?.[manager.goal] ?? manager.goal} · ${currency}`;
};

export const ExperimentStatisticsScreen = ({ experiment, onDeployed }: ExperimentStatisticsScreenProps) => {
  const manager = useExperimentStats(experiment);
  // The results AI (asked from the header, on any tab) reads the numbers chosen here.
  useReportResultsAiView({ experimentId: experiment.id, view: resultsView(manager), label: viewLabel(manager) });
  const { isLoading } = manager;
  const chartRef = useRef<HTMLDivElement>(null);
  // Watched by the sticky bar: once this card scrolls off the top, the bar appears.
  const evaluationRef = useRef<HTMLDivElement>(null);
  const { chartData, categories } = processChartData(manager.chartDataRaw, manager.goal);

  /*
    One colour per variant for the whole screen, ranked against the experiment's full
    variant list. Charts here plot different subsets — the timeseries endpoint behind
    RateOverTime drops deployed variants — and colouring by position within each chart's
    own array meant a variant moved slot whenever a chart excluded one before it.
  */
  const colorOrder = variantColorOrder(experiment.variants ?? []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="supporting">Loading statistics...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <StickyControlBar manager={manager} watch={evaluationRef} />

      <div className="flex flex-col gap-6">
        {/*
        Verdict first, evidence second.

        The recommendation states the conclusion; how results are judged,
        significance and power are what back it up, so they follow it directly
        rather than sitting below the raw numbers. The statistics table and charts
        come last as the underlying detail.

        Objective and currency stay at the top: they change what is being measured,
        so they govern every block below them, including significance.
      */}
        <Selectors manager={manager} chartData={chartData} categories={categories} chartRef={chartRef} />
        <SampleRatioCheck experiment={experiment} manager={manager} />
        <RecommendationBanner recommendation={manager.recommendation} />
        <div ref={evaluationRef}>
          <EvaluationControls manager={manager} />
        </div>
        {/*
          Collapsible from here down. The validity check, recommendation and evaluation
          controls stay fixed: they are short, and they govern how everything below is
          read, so hiding them would let someone draw conclusions from a table without
          seeing what it was judged against.
        */}
        <ReportSection
          id="significance"
          title="Significance"
          summary={significanceSummary(manager)}
          tourAnchor="results-significance"
        >
          <SignificanceTable experiment={experiment} manager={manager} />
        </ReportSection>

        <ReportSection id="power" title="Power analysis" summary={powerSummary(manager)} tourAnchor="results-power">
          <PowerAnalysis power={manager.power} totalSessions={manager.totalSessions} experiment={experiment} />
        </ReportSection>

        <ReportSection
          id="statistics"
          title="Statistics"
          summary={`${manager.totalSessions.toLocaleString()} sessions`}
          tourAnchor="results-statistics"
        >
          <StatisticsTable experiment={experiment} manager={manager} onDeployed={onDeployed} />
        </ReportSection>

        {/*
          After the primary's own numbers, before the charts: it answers "does the rest of
          the funnel agree with this result", which only means something once the result
          itself has been read.
        */}
        <ReportSection
          id="secondary"
          title="Secondary objectives"
          summary={secondarySummary(manager)}
          tourAnchor="results-secondary"
        >
          <SecondaryObjectives manager={manager} />
        </ReportSection>

        <ReportSection id="charts" title="Charts over time" tourAnchor="results-charts">
          <ChartTabs
            chartDataRaw={manager.chartDataRaw}
            goal={manager.goal}
            goalLabel={manager.goalLabels?.[manager.goal]}
            chartRef={chartRef}
            colorOrder={colorOrder}
          />
        </ReportSection>

        <ReportSection id="rate-over-time" title="Rate over time" tourAnchor="results-rate-over-time">
          <RateOverTime experiment={experiment} manager={manager} />
        </ReportSection>

        {/*
          Last on the page: per-visitor detail for websites with the visitor ID flag on. It
          never feeds the verdict above, and it renders nothing without visitor-id data.
        */}
        <Segments experiment={experiment} manager={manager} />
      </div>
    </div>
  );
};
